import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { MonthCalendar } from './MonthCalendar'
import { ListCalendar } from './ListCalendar'
import { Button } from '@/components/ui/shadcn/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Event } from '@/lib/calendar/types'
import { scheduledPostToEvent, trackedPostToEvent } from '@/lib/calendar/post-adapter'
import { trpc } from '@/config/trpc'
import { useSharedView } from '@/context/sharedView'
import { toast } from 'sonner'
import { type PlatformType } from '@backend/lib/db/schema/platform'

type StatusFilter = 'all' | 'scheduled' | 'published' | 'imported' | 'failed'
type PlatformFilter = 'all' | PlatformType

interface ScheduledPostsCalendarProps {
  view: 'timeline' | 'calendar'
  status: StatusFilter
  platform: PlatformFilter
  onSelectEvent: (event: Event) => void
}

export function ScheduledPostsCalendar({
  view,
  status,
  platform,
  onSelectEvent,
}: ScheduledPostsCalendarProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const dateParam = searchParams.get('date')
    if (dateParam) {
      const parsed = new Date(dateParam + 'T00:00:00')
      if (!isNaN(parsed.getTime())) return parsed
    }
    return new Date()
  })

  // Clean up the date query param after using it
  useEffect(() => {
    if (searchParams.has('date')) {
      setSearchParams(
        (prev) => {
          prev.delete('date')
          return prev
        },
        { replace: true },
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { isShared } = useSharedView()
  const utils = trpc.useUtils()
  const { data: scheduledPosts, isLoading: loadingScheduled } = trpc.scheduledPost.list.useQuery()
  const { data: trackedPosts, isLoading: loadingTracked } = trpc.trackedPost.list.useQuery()
  const { mutateAsync: updateScheduledPost } = trpc.scheduledPost.update.useMutation()
  const isLoading = loadingScheduled || loadingTracked

  const matchesPlatform = (p: PlatformType) => platform === 'all' || p === platform

  const scheduledEvents: Event[] = (scheduledPosts ?? [])
    .filter((post) => {
      // Published scheduled posts are represented by their tracked_post counterpart
      if (post.status === 'published') return false
      if (!matchesPlatform(post.platform)) return false
      if (status === 'all') return true
      if (status === 'scheduled') return post.status === 'scheduled' || post.status === 'publishing'
      if (status === 'failed') return post.status === 'failed'
      return false
    })
    .map(scheduledPostToEvent)

  const trackedEvents: Event[] = (trackedPosts ?? [])
    .filter((post) => {
      if (!matchesPlatform(post.platform as PlatformType)) return false
      if (status === 'all') return true
      if (status === 'published') return post.source === 'platform'
      if (status === 'imported') return post.source === 'import'
      return false
    })
    .map(trackedPostToEvent)

  const events = [...scheduledEvents, ...trackedEvents]

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const getMonthYearText = () => {
    return `${monthNames[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
  }

  const handlePrev = () => {
    const newDate = new Date(selectedDate)
    newDate.setMonth(newDate.getMonth() - 1)
    setSelectedDate(newDate)
  }

  const handleNext = () => {
    const newDate = new Date(selectedDate)
    newDate.setMonth(newDate.getMonth() + 1)
    setSelectedDate(newDate)
  }

  const handleMoveEvent = async (eventId: string, newDate: Date) => {
    const event = events.find((e) => e.id === eventId)
    if (!event || event.source === 'tracked') return

    const [hours, minutes] = event.time.split(':').map(Number)
    newDate.setHours(hours ?? 0, minutes ?? 0, 0, 0)

    if (newDate < new Date()) {
      toast.warning('Cannot schedule post in the past', {
        description: 'The scheduled time has already passed. Please choose a later time or date.',
      })
      return
    }

    const updatePayload = {
      id: event.postId || parseInt(eventId),
      scheduledAt: newDate,
    }

    try {
      await updateScheduledPost(updatePayload)
      utils.scheduledPost.list.invalidate()
      toast.success('Post rescheduled successfully')
    } catch (error) {
      toast.error('Failed to reschedule post', { description: String(error) })
    }
  }

  // Inline edits inside ListCalendar (e.g. time change) bypass the modal — they
  // need to write through directly so the change persists without opening the dialog.
  const handleInlineUpdate = async (updatedEvent: Event) => {
    const [hours, minutes] = updatedEvent.time.split(':').map(Number)
    const newDate = new Date(updatedEvent.date)
    newDate.setHours(hours ?? 0, minutes ?? 0, 0, 0)
    try {
      await updateScheduledPost({
        id: updatedEvent.postId ?? parseInt(updatedEvent.id),
        scheduledAt: newDate,
      })
      utils.scheduledPost.list.invalidate()
    } catch (error) {
      toast.error('Failed to update post', { description: String(error) })
    }
  }

  const handleViewPost = (event: Event) => {
    if (!event.link) {
      toast.warning('Post not available', {
        description: `This post may have been archived or deleted on ${event.platform}.`,
      })
      return
    }

    try {
      window.open(event.link, '_blank', 'noopener,noreferrer')
    } catch (_error) {
      toast.error('Failed to open post', {
        description: `Unable to open the post on ${event.platform}. The post may have been archived or deleted.`,
      })
    }
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleDragEnd = (dragEvent: DragEndEvent) => {
    if (isShared) return
    const { active, over } = dragEvent
    if (!over) return

    const activeData = active.data.current as { event: Event } | undefined
    const overData = over.data.current as { date: Date } | undefined

    if (!activeData?.event || !overData?.date) return

    const targetDate = new Date(overData.date)
    targetDate.setHours(0, 0, 0, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (targetDate < today) return

    handleMoveEvent(activeData.event.id, new Date(overData.date))
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">Loading calendar...</div>
      </div>
    )
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="flex h-full w-full flex-col">
        <div className="z-5 relative mb-3 flex shrink-0 items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePrev} className="h-8 w-8">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-foreground min-w-[160px] text-center text-sm font-medium">
            {getMonthYearText()}
          </h2>
          <Button variant="outline" size="icon" onClick={handleNext} className="h-8 w-8">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {view === 'calendar' ? (
          <div className="bg-background border-border shadow-xs relative z-10 flex min-h-0 flex-1 overflow-hidden rounded-lg border p-4">
            <MonthCalendar
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              events={events}
              onMoveEvent={handleMoveEvent}
              onEventClick={onSelectEvent}
              onViewPost={handleViewPost}
            />
          </div>
        ) : (
          <div className="bg-background border-border shadow-xs relative z-10 min-h-0 flex-1 overflow-y-auto rounded-lg border p-6">
            <ListCalendar
              selectedDate={selectedDate}
              events={events}
              onEventClick={onSelectEvent}
              onMoveEvent={handleMoveEvent}
              onUpdateEvent={handleInlineUpdate}
              onViewPost={handleViewPost}
            />
          </div>
        )}
      </div>
    </DndContext>
  )
}
